import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';

/**
 * Dedicated, chrome-less print view for a Delhivery shipping label. Fetches the
 * label PDF (authenticated), embeds it full-screen, and auto-opens the print
 * dialog once it loads. Opened in its own tab from the Shipping page.
 */
export function LabelPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const printed = useRef(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await api.get(`/shipping/shipments/${id}/label.pdf`, { responseType: 'blob' });
        // Force the PDF MIME type so the iframe renders it inline (some responses
        // arrive as application/octet-stream, which the browser would download).
        const pdfBlob = new Blob([res.data as Blob], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(pdfBlob);
        setUrl(objectUrl);
      } catch {
        setError('Could not load the shipping label. The shipment may not be booked yet.');
      }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);

  const triggerPrint = () => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    try { w.focus(); w.print(); } catch { window.print(); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 print:hidden">
        <span className="text-sm font-bold">Shipping Label — AWB #{id}</span>
        <div className="flex gap-2">
          <button onClick={triggerPrint} className="rounded bg-[#c2410c] px-3 py-1.5 text-xs font-semibold text-white">Print</button>
          <button onClick={() => window.close()} className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold">Close</button>
        </div>
      </div>
      {error && <div className="p-6 text-sm text-red-600">{error}</div>}
      {url && (
        <iframe
          ref={iframeRef}
          src={url}
          title="Shipping label"
          style={{ flex: 1, width: '100%', border: 'none' }}
          onLoad={() => { if (!printed.current) { printed.current = true; setTimeout(triggerPrint, 400); } }}
        />
      )}
      {!url && !error && <div className="p-6 text-sm text-gray-500">Loading label…</div>}
    </div>
  );
}
